import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";

/**
 * Enumeration of different available templates to be used when generating a
 * pdf template (each of these will need to be implemented directly in the
 * client that's generating the pdf).
 */
export enum ContentElementPdfTemplates {
    ARTICLE,
    BOOK,
    BOOK_LANGUAGE,
    SOURCE,
    MUNKSGAARD,
    HANS_REITZEL,
}

/**
 * Specification of data needed to instantiate a new model.
 */
export type ContentElementData = ElementModelData & {
    toolbar?: {
        enableFavorite: boolean;
        enableLightbox: boolean;
        enableNewWindow: boolean;
        enableNotebook: boolean;
        enablePdf: boolean;
        enableSpeech: boolean;
    };
    isStandalone?: boolean;
    learningObject?: {
        id: string;
        title: string;
        type: string; // tslint:disable-line no-reserved-keywords
    };
    ribbon?: string;
    theme?: string;
    pdf?: {
        lineCount?: boolean;
        template?: ContentElementPdfTemplates;
        headerImage?: string;
        file?: string;
    };
    speechFile?: string;

};

/**
 * Core class for all content elements describing shared data
 */
export abstract class ContentElementModel extends ElementModel {
    /**
     * Specifies configurations of the toolbar that's supposed to be rendered
     * for this element.
     */
    public readonly toolbar: {
        enableFavorite: boolean;
        enableLightbox: boolean;
        enableNewWindow: boolean;
        enableNotebook: boolean;
        enablePdf: boolean;
        enableSpeech: boolean;
    };

    /**
     * Specifies if this element is defined as a "stand alone" element (ie. an
     * element that would make sense, even if taken out of it's context).
     */
    public readonly isStandalone?: boolean;

    /**
     * Describes this element as a learning object (which is useful when
     * interacting with external apis such as UserData).
     */
    public readonly learningObject?: {
        id: string;
        title: string;
        type: string; // tslint:disable-line no-reserved-keywords
    };

    /**
     * Specifies the character to use to render the ribbon in the top-left
     * corner of the element (this will be directly mapped against the icon font
     * that's hosted in the pm2 backend).
     */
    public readonly ribbon?: string;

    /**
     * Specifies the theme that should be applied to this element (ie. a css
     * class).
     */
    public readonly theme?: string;

    /**
     * Specifies configurations when generating pdf output based on the content
     * of this element.
     */
    public readonly pdf: {
        /**
         * Specifies if line count should be enabled in the left-hand side of
         * the pdf.
         */
        lineCount?: boolean;

        /**
         * Optionally specifies a template to be used when generating the pdf
         * based on the given content.
         */
        template?: ContentElementPdfTemplates;

        /**
         * Optionally specifies an absolute url to a header image that should be
         * inserted in the generated pdf.
         */
        headerImage?: string;

        /**
         * Optionally specifies an absolute url to a pre-created pdf file that
         * should be downloaded rather than generating one on the fly.
         */
        file?: string;
    };

    /**
     * Optionally specifies an absolute url to an audio file that should be used
     * when reading the content of the element out loud, rather than generating
     * a speech synthesis on the fly.
     */
    public readonly speechFile?: string;

    /**
     * Apply initial data when a ContentElementModel is being created.
     */
    constructor(initial: ContentElementData) {
        super(initial);

        this.toolbar = initial.toolbar || {
            enableFavorite: false,
            enableLightbox: false,
            enableNewWindow: false,
            enableNotebook: false,
            enablePdf: false,
            enableSpeech: false,
        };

        this.isStandalone = initial.isStandalone;
        this.learningObject = initial.learningObject;
        this.ribbon = initial.ribbon;
        this.theme = initial.theme;
        this.pdf = initial.pdf || {};
        this.speechFile = initial.speechFile;
    }
}
